import { supabase } from '../lib/supabase';
import { AuditResult, AuditInput } from '../types';
import { pricingService } from './pricingService';
import { PricingSnapshotSchema } from '../lib/validation';

export const auditService = {
  async saveAudit(input: AuditInput, result: AuditResult): Promise<string | null> {
    try {
      // 1. Serialize and validate current pricing snapshot
      const snapshot = pricingService.serializePricingSnapshot();
      PricingSnapshotSchema.parse(snapshot);

      // 2. Resolve pricing_snapshot ID by matching hash
      let pricingSnapshotId: string | undefined = undefined;
      const { data: existingSnapshot, error: findError } = await supabase
        .from('pricing_snapshots')
        .select('id')
        .eq('snapshot_hash', snapshot.version)
        .maybeSingle();

      if (findError) {
        console.error('Error querying existing pricing snapshot:', findError);
      }

      if (existingSnapshot) {
        pricingSnapshotId = existingSnapshot.id;
      } else {
        const { data: newSnapshot, error: insertSnapshotError } = await supabase
          .from('pricing_snapshots')
          .insert({
            snapshot_hash: snapshot.version,
            snapshot_json: snapshot,
          })
          .select('id')
          .single();

        if (insertSnapshotError) {
          console.error('Error creating new pricing snapshot:', insertSnapshotError);
        } else if (newSnapshot) {
          pricingSnapshotId = newSnapshot.id;
        }
      }

      // 3. Save the audit record including both snapshot fields
      const { data, error } = await supabase
        .from('audits')
        .insert({
          team_size: input.teamSize,
          primary_use_case: input.useCase || 'General Engineering Optimization',
          tools_json: input.tools,
          recommendations_json: result.recommendations,
          monthly_savings: result.potentialSavings,
          annual_savings: result.potentialSavings * 12,
          ai_summary: result.aiSummary,
          total_monthly_spend: result.totalMonthlySpend,
          efficiency_metrics: result.metrics,
          pricing_snapshot_id: pricingSnapshotId,
          pricing_snapshot_json: snapshot,
        })
        .select('public_id')
        .single();

      if (error) {
        console.error('Error saving audit:', error);
        return null;
      }

      return data?.public_id;
    } catch (err) {
      console.error('Failed to complete persistent audit flow:', err);
      return null;
    }
  },

  async getAuditByPublicId(publicId: string): Promise<AuditResult | null> {
    const { data, error } = await supabase
      .from('audits')
      .select('*')
      .eq('public_id', publicId)
      .single();

    if (error || !data) {
      console.error('Error fetching audit:', error);
      return null;
    }

    return {
      id: data.id.toString(),
      publicId: data.public_id,
      createdAt: data.created_at,
      totalMonthlySpend: data.total_monthly_spend,
      potentialSavings: data.monthly_savings,
      recommendations: data.recommendations_json,
      metrics: data.efficiency_metrics,
      aiSummary: data.ai_summary,
      pricingSnapshotId: data.pricing_snapshot_id,
      pricingSnapshotJson: data.pricing_snapshot_json,
    };
  },

  async captureLead(auditId: string, email: string, companyName?: string, role?: string) {
    const { error } = await supabase
      .from('leads')
      .insert({
        audit_id: auditId,
        email,
        company_name: companyName,
        role,
      });

    if (error) {
      console.error('Error capturing lead:', error);
      throw error;
    }
  }
};
